import { CircularProgress } from '@mui/material';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Backdrop, RichEditor, Spinner, Table } from 'components';
import { NewConfirmDialog } from 'components/Dialogs';
import { DEACTIVATE_USER_COMMENT_MAX_LENGTH } from 'constants/fieldsLength';
import { EditEmployeeForm } from 'features/employeeDetails';
import { useWindowSize } from 'hooks';
import {
  EmployeesMobileTableFields,
  EmployeesTableFields,
  TableColumnsConfig,
  UserStatus,
} from 'types';

import { EmployeesTableHeader } from '../EmployeesTableHeader';
import {
  DEFAULT_EMPLOYEES_FIELDS,
  MOBILE_EMPLOYEES_FIELDS,
  MOBILE_OFFBOARDING_EMPLOYEES_FIELDS,
  OFFBOARDING_EMPLOYEES_FIELDS,
} from './config';
import { useEmployeeTable } from './hooks';
import { OffBoardWrapper, ProgressW, RichEditorW } from './styled';
import { headerItemSx } from './styles';

interface EmployeesTableProps {
  isOffboarding?: boolean;
}

export function EmployeesTable({ isOffboarding }: EmployeesTableProps): ReactElement | null {
  const {
    isUsersListLoading,
    usersList,
    lastListRef,
    isOpenModal,
    handleCloseModal,
    onDeactivateEmployee,
    isFetchingNextPage,
    deleteEmployee,
    setDeletedComment,
    deletedCommentError,
    onHandleFocusRichEditor,
    handleRowClick,
    isOpenActivateModal,
    handleCloseActivateModal,
    user,
    isLoadingUser,
    isManager,
    userProfileFields,
  } = useEmployeeTable(Boolean(isOffboarding));
  const { isMobile } = useWindowSize();
  const { t } = useTranslation();

  const isOffboardingActiveUser = isOffboarding && deleteEmployee?.status == UserStatus.Active;
  const confirmDialogTitle = isOffboardingActiveUser
    ? t('employee.table.modal.title.offboard', { userName: deleteEmployee?.userName || '' })
    : t('employee.table.modal.title.remove');

  const confirmDialogCloseText = isOffboardingActiveUser
    ? t('employee.table.modal.subtitle.offboard')
    : t('employee.table.modal.subtitle.remove');

  if (usersList.length === 0 && isUsersListLoading) {
    return <Spinner />;
  }

  const webColumns = isOffboarding
    ? OFFBOARDING_EMPLOYEES_FIELDS
    : DEFAULT_EMPLOYEES_FIELDS(isManager);
  const mobileColumns = isOffboarding
    ? MOBILE_OFFBOARDING_EMPLOYEES_FIELDS
    : MOBILE_EMPLOYEES_FIELDS(isManager);

  const columns = isMobile ? mobileColumns : webColumns;

  const filteredColumns = (columns as TableColumnsConfig<EmployeesMobileTableFields>[]).filter(
    (column) =>
      userProfileFields.find((field) => !column.filterKey || field.label === column.filterKey),
  );

  return (
    <>
      {!isOpenActivateModal && (
        <OffBoardWrapper>
          {isLoadingUser && <Backdrop />}
          {!isOffboarding && <EmployeesTableHeader />}

          {usersList.length > 0 ? (
            <Table<EmployeesTableFields, string>
              isLoading={isUsersListLoading}
              columns={filteredColumns}
              data={usersList}
              headerItemSx={headerItemSx}
              lastListRef={lastListRef}
              onClickRow={handleRowClick}
            />
          ) : (
            <></>
          )}

          {usersList.length && isFetchingNextPage && (
            <ProgressW>
              <CircularProgress color="info" size={60} />
            </ProgressW>
          )}
        </OffBoardWrapper>
      )}

      <NewConfirmDialog
        sx={{ '.MuiDialog-paper': { paddingBottom: '56px' } }}
        title={confirmDialogTitle}
        subTitle={confirmDialogCloseText}
        open={isOpenModal}
        onClose={handleCloseModal}
        onConfirm={onDeactivateEmployee}
        isLoading={false}
        isContentOverflow
        disableEnforceFocus
        submitLabel={
          isOffboardingActiveUser
            ? t('employee.table.modal.offboardSuccessButton')
            : t('employee.table.modal.successButton')
        }
        cancelLabel={t('employee.table.modal.cancelButton')}
      >
        {isOffboarding &&
          (deleteEmployee?.status == UserStatus.Active ||
            deleteEmployee?.status == UserStatus.Registered) && (
            <RichEditorW>
              <RichEditor
                placeholder={t('employee.table.modal.stateReason')}
                maxLength={DEACTIVATE_USER_COMMENT_MAX_LENGTH}
                errorText={deletedCommentError}
                onChange={setDeletedComment}
                onFocus={onHandleFocusRichEditor}
                onBlur={onHandleFocusRichEditor}
                isModal
              />
            </RichEditorW>
          )}
      </NewConfirmDialog>

      {user && isOpenActivateModal && (
        <EditEmployeeForm
          open={!!user && isOpenActivateModal}
          onClose={handleCloseActivateModal}
          employee={user}
          key={user.id}
          formTitle={t('activateEmployee.formTitle')}
          mode="activate"
        />
      )}
    </>
  );
}
